import type { FC } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Modal, ModalInner } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';

export interface CardPinModalViewProps {
  lastFourDigits: string;
  pin: string | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onClose: () => void;
}

export const CardPinModalView: FC<CardPinModalViewProps> = ({
  lastFourDigits,
  pin,
  loading,
  error,
  onRetry,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Modal open handleClose={onClose}>
      <ModalInner>
        <ModalCard>
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {t('myCards.pin.label')}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
              >
                •••• {lastFourDigits}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label={t('actions.close')}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Stack
            spacing={2}
            sx={{ alignItems: 'center', justifyContent: 'center', mt: 3 }}
          >
            {error ? (
              <>
                <Typography sx={{ color: 'error.main' }}>
                  {t('myCards.pin.loadError')}
                </Typography>
                <Button variant="outlined" onClick={onRetry} disabled={loading}>
                  {t('myCards.pin.retry')}
                </Button>
              </>
            ) : loading || pin === undefined ? (
              <CircularProgress />
            ) : (
              <Stack direction="row" spacing={1.5} data-cy="card-pin-digits">
                {pin.split('').map((digit, index) => (
                  <Digit key={index} data-testid="card-pin-digit">
                    {digit}
                  </Digit>
                ))}
              </Stack>
            )}
          </Stack>
        </ModalCard>
      </ModalInner>
    </Modal>
  );
};

const ModalCard = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    p: 3,
    minWidth: { xs: '100%', sm: 400 },
    minHeight: 220,
  })
);

const Digit = styled(Typography)(({ theme }) =>
  theme.unstable_sx({
    width: 64,
    py: 1.5,
    textAlign: 'center',
    fontFamily: 'monospace',
    fontSize: '3rem',
    fontWeight: 'fontWeightBold',
    lineHeight: 1.2,
    borderRadius: 2,
    bgcolor: 'background.neutral',
    border: 1,
    borderColor: 'divider',
  })
);
